<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height overflow-auto"
      type="bar"
      :height="220"
      :raw-data="speedZones"
      :chart-value-keys="[ { key: 'value', type: 'bar'} ]"
      :plot-options="{ bar: { horizontal: true, borderRadius: 4 } }"
      x-value="key"
      :grid="{ show: false }"
      :xaxis="{
        categories: speedZones.map(stat => stat.label),
        tickPlacement: 'between',
        title: { text: $t('chart_legends.distance') },
        axisBorder: { show: false }
      }"
    >
      <template v-slot:header>
        <v-card-title class="pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div
                class="subtitle-2 grey--text text--grey-100"
                style="word-break: normal"
              >
                {{ $t("card_title.distance_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="ml-3"
            >
              <v-btn
                v-if="removable"
                icon
                style="pointer-events: auto"
                @click="$emit('remove')"
              >
                <v-icon small>
                  fal fa-times
                </v-icon>
              </v-btn>
              <visual-list-toggle
                v-else
                :value.sync="viewMode"
              />
            </v-col>
          </v-row>
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-4">
        <v-row
          no-gutters
          align="center"
        >
          <v-col>
            <div
              class="subtitle-2 grey--text text--grey-100"
              style="word-break: normal"
            >
              {{ $t("card_title.distance_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
            </div>
          </v-col>
          <v-col
            cols="auto"
            class="ml-3"
          >
            <visual-list-toggle
              v-if="!removable"
              :value.sync="viewMode"
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="speedZones"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        fixed-header
        dense
        :height="220"
      />
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

export default {
  name: 'DistanceZoneChart',
  components: { LineChart, VisualListToggle },
  props: {
    stats: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    speedZones() {
      if (hasSixZoneSetup()) {
        return [
          { key: 'TDZONE6', label: this.$t('variables.dist_zone_n', { n: 6 }), value: this.getStatValue('TDZONE6') || 0 },
          { key: 'CALETLDLSP', label: this.$t('variables.dist_zone_n', { n: 5 }), value: this.getStatValue('CALETLDLSP') || 0 },
          { key: 'CALETLDLHSR', label: this.$t('variables.dist_zone_n', { n: 4 }), value: this.getStatValue('CALETLDLHSR') || 0 },
          { key: 'CACTCSVB3TD', label: this.$t('variables.dist_zone_n', { n: 3 }), value: this.getStatValue('CACTCSVB3TD') || 0 },
          { key: 'CACTCSVB2TD', label: this.$t('variables.dist_zone_n', { n: 2 }), value: this.getStatValue('CACTCSVB2TD') || 0 },
          { key: 'CACTCSVB1TD', label: this.$t('variables.dist_zone_n', { n: 1 }), value: this.getStatValue('CACTCSVB1TD') || 0 }
        ]
      }
      return [
        { key: 'CALETLDLSP', label: this.$t('variables.CALETLDLSP'), value: this.getStatValue('CALETLDLSP') || 0 },
        { key: 'CALETLDLHSR', label: this.$t('variables.CALETLDLHSR'), value: this.getStatValue('CALETLDLHSR') || 0 },
        { key: 'CACTCSVB3TD', label: this.$t('variables.CACTCSVB3TD'), value: this.getStatValue('CACTCSVB3TD') || 0 },
        { key: 'CACTCSVB2TD', label: this.$t('variables.CACTCSVB2TD'), value: this.getStatValue('CACTCSVB2TD') || 0 },
        { key: 'CACTCSVB1TD', label: this.$t('variables.CACTCSVB1TD'), value: this.getStatValue('CACTCSVB1TD') || 0 }
      ]
    },
    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.zone'),
          value: 'label',
          sortable: true,
          width: '60%'
        },
        {
          text: this.$t('table_headers.distance'),
          value: 'value',
          sortable: true,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getKeyWithAggregation(key) {
      return this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key
    },
    getStatValue(key) {
      const aggregatedKey = this.getKeyWithAggregation(key)
      return this.stats[aggregatedKey]
    }
  }
}
</script>
